/**
 * MarkdownWithAssets Component
 *
 * Renders markdown content with embedded chart and table components.
 * Parses [CHART:xxx] and [TABLE:xxx] references and replaces them with actual React components.
 */

import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChartDataResult } from '../../hooks/useChartData';

// NEW Chart components (matching PDF report format 04-10)
import Grafico04 from './charts/Grafico04';
import Grafico05 from './charts/Grafico05';
import Grafico06 from './charts/Grafico06';
import Grafico07V2 from './charts/Grafico07_v2';
import Grafico08V2 from './charts/Grafico08_v2';
import Grafico09V2 from './charts/Grafico09_v2';
import Grafico10V2 from './charts/Grafico10_v2';

// Legacy chart components (kept for backwards compatibility with grafico11-13)
import Grafico11 from './charts/Grafico11';
import Grafico12 from './charts/Grafico12';
import Grafico13 from './charts/Grafico13';

// Table components (legacy)
import Cuadro04 from './tables/Cuadro04';
import Cuadro05 from './tables/Cuadro05';
import Anexo1 from './tables/Anexo1';

// NEW Table component (matching PDF format)
import Cuadro06 from './tables/Cuadro06';

import './MarkdownWithAssets.css';

interface MarkdownWithAssetsProps {
  content: string;
  chartData?: ChartDataResult | null;
  isLoading?: boolean;
}

type Segment =
  | { type: 'text'; content: string }
  | { type: 'asset'; assetType: 'CHART' | 'TABLE'; assetId: string };

/**
 * Parse markdown content into segments of text and asset references
 */
function parseContent(content: string): Segment[] {
  const assetPattern = /\[(CHART|TABLE):(\w+)\]/g;
  const segments: Segment[] = [];
  let lastIndex = 0;
  let match;

  while ((match = assetPattern.exec(content)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: content.slice(lastIndex, match.index),
      });
    }
    // Add asset reference
    segments.push({
      type: 'asset',
      assetType: match[1] as 'CHART' | 'TABLE',
      assetId: match[2],
    });
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < content.length) {
    segments.push({
      type: 'text',
      content: content.slice(lastIndex),
    });
  }

  return segments;
}

/**
 * Render an asset (chart or table) component
 */
function renderAsset(
  assetId: string,
  chartData: ChartDataResult | null | undefined,
  isLoading: boolean,
  key: number
): React.ReactNode {
  // Show loading placeholder if data is still loading
  if (isLoading) {
    return (
      <div key={key} className="asset-loading">
        <div className="asset-loading-spinner" />
        <span>Loading {assetId}...</span>
      </div>
    );
  }

  // Show placeholder if no data available
  if (!chartData) {
    return (
      <div key={key} className="asset-placeholder">
        <span className="asset-placeholder-icon">📊</span>
        <span>No data available for {assetId}</span>
      </div>
    );
  }

  // Render the appropriate component based on asset ID
  switch (assetId.toLowerCase()) {
    // Charts 01-03: Not yet implemented - show placeholder
    case 'grafico01':
    case 'grafico02':
    case 'grafico03':
      return (
        <div key={key} className="asset-not-implemented">
          <span className="asset-not-implemented-icon">📊</span>
          <span>{assetId} - Chart not yet implemented</span>
        </div>
      );

    // NEW Charts matching PDF report format (04-10)
    case 'grafico04':
      return chartData.grafico04_new?.length ? (
        <div key={key} className="embedded-chart">
          <Grafico04 data={chartData.grafico04_new} />
        </div>
      ) : (
        <AssetNoData key={key} assetId={assetId} />
      );

    case 'grafico05':
      return chartData.grafico05_new?.length ? (
        <div key={key} className="embedded-chart">
          <Grafico05 data={chartData.grafico05_new} />
        </div>
      ) : (
        <AssetNoData key={key} assetId={assetId} />
      );

    case 'grafico06':
      return chartData.grafico06_new?.length ? (
        <div key={key} className="embedded-chart">
          <Grafico06 data={chartData.grafico06_new} />
        </div>
      ) : (
        <AssetNoData key={key} assetId={assetId} />
      );

    case 'grafico07':
      return chartData.grafico07_new?.length ? (
        <div key={key} className="embedded-chart">
          <Grafico07V2 data={chartData.grafico07_new} />
        </div>
      ) : (
        <AssetNoData key={key} assetId={assetId} />
      );

    case 'grafico08':
      return chartData.grafico08_new?.length ? (
        <div key={key} className="embedded-chart">
          <Grafico08V2 data={chartData.grafico08_new} />
        </div>
      ) : (
        <AssetNoData key={key} assetId={assetId} />
      );

    case 'grafico09':
      return chartData.grafico09_new?.length ? (
        <div key={key} className="embedded-chart">
          <Grafico09V2 data={chartData.grafico09_new} />
        </div>
      ) : (
        <AssetNoData key={key} assetId={assetId} />
      );

    case 'grafico10':
      return chartData.grafico10_new?.length ? (
        <div key={key} className="embedded-chart">
          <Grafico10V2 data={chartData.grafico10_new} />
        </div>
      ) : (
        <AssetNoData key={key} assetId={assetId} />
      );

    // Legacy charts (11-13) - kept for backwards compatibility
    case 'grafico11':
      return chartData.grafico11?.length ? (
        <div key={key} className="embedded-chart">
          <Grafico11 data={chartData.grafico11} />
        </div>
      ) : (
        <AssetNoData key={key} assetId={assetId} />
      );

    case 'grafico12':
      return chartData.grafico12?.length ? (
        <div key={key} className="embedded-chart">
          <Grafico12 data={chartData.grafico12} />
        </div>
      ) : (
        <AssetNoData key={key} assetId={assetId} />
      );

    case 'grafico13':
      return chartData.grafico13?.length ? (
        <div key={key} className="embedded-chart">
          <Grafico13 data={chartData.grafico13} />
        </div>
      ) : (
        <AssetNoData key={key} assetId={assetId} />
      );

    // Tables 01-03: Not yet implemented - show placeholder
    case 'cuadro01':
    case 'cuadro02':
    case 'cuadro03':
      return (
        <div key={key} className="asset-not-implemented">
          <span className="asset-not-implemented-icon">📋</span>
          <span>{assetId} - Table not yet implemented</span>
        </div>
      );

    // Tables 04-05: Legacy (kept for backwards compatibility)
    case 'cuadro04':
      return chartData.cuadro04?.length ? (
        <div key={key} className="embedded-table">
          <Cuadro04 data={chartData.cuadro04} />
        </div>
      ) : (
        <AssetNoData key={key} assetId={assetId} />
      );

    case 'cuadro05':
      return chartData.cuadro05?.length ? (
        <div key={key} className="embedded-table">
          <Cuadro05 data={chartData.cuadro05} />
        </div>
      ) : (
        <AssetNoData key={key} assetId={assetId} />
      );

    // NEW Table matching PDF format - Cuadro 06: Evolución Balance
    case 'cuadro06':
      return chartData.cuadro06?.length ? (
        <div key={key} className="embedded-table">
          <Cuadro06 data={chartData.cuadro06} />
        </div>
      ) : (
        <AssetNoData key={key} assetId={assetId} />
      );

    case 'anexo01':
    case 'anexo1':
      return chartData.anexo1?.length ? (
        <div key={key} className="embedded-table">
          <Anexo1 data={chartData.anexo1} />
        </div>
      ) : (
        <AssetNoData key={key} assetId={assetId} />
      );

    default:
      return (
        <div key={key} className="asset-unknown">
          <span className="asset-unknown-icon">❓</span>
          <span>Unknown asset: {assetId}</span>
        </div>
      );
  }
}

/**
 * Helper component for missing data
 */
function AssetNoData({ assetId }: { assetId: string }) {
  return (
    <div className="asset-no-data">
      <span className="asset-no-data-icon">📊</span>
      <span>No data available for {assetId}</span>
    </div>
  );
}

/**
 * Main component
 */
export default function MarkdownWithAssets({
  content,
  chartData,
  isLoading = false,
}: MarkdownWithAssetsProps) {
  // Parse content into segments
  const segments = useMemo(() => parseContent(content || ''), [content]);

  // If no content, show placeholder
  if (!content) {
    return (
      <div className="markdown-with-assets">
        <p className="no-content">*No content yet*</p>
      </div>
    );
  }

  return (
    <div className="markdown-with-assets">
      {segments.map((segment, idx) => {
        if (segment.type === 'text') {
          return (
            <ReactMarkdown key={idx} remarkPlugins={[remarkGfm]}>
              {segment.content}
            </ReactMarkdown>
          );
        }
        // Render asset component
        return renderAsset(segment.assetId, chartData, isLoading, idx);
      })}
    </div>
  );
}
